import * as React from "react";
import { UitkSheet, UitkSheetContent } from "uitk-react-sheet";
import { UitkToolbar } from "uitk-react-toolbar";
import { UitkText } from "uitk-react-text";
import { UitkTable, UitkTableBody, UitkTableHead, UitkTableRow, UitkTableCell } from "uitk-react-tables";
import { TripCostEstimatorHotelCost } from "../typings";
import { observer } from "mobx-react";
import { ItemKeyHelper } from "src/components/utility/ItemKeyHelper";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";
import { useLocalization } from "@shared-ui/localization-context";

export const ListItem = (props: { text: string; prices: number[]; onClick: () => void }) => {
  const somePriceIsEmpty = props.prices.some((item) => !item);
  const pricesRange: string = props.prices.map((item) => `$${item}`).join(" - ");
  const Link = () => (
    <TrackedLink className="Link" onClick={props.onClick} moduleName="tripCostEstimator" rfrr="dialog.monthList">
      {pricesRange}
    </TrackedLink>
  );

  const { formatText } = useLocalization();

  const contentText = somePriceIsEmpty ? formatText("tripCostEstimator.noData") : <Link />;

  return (
    <UitkTableRow>
      <UitkTableCell>
        <UitkText>{props.text}</UitkText>
      </UitkTableCell>
      <UitkTableCell>
        <UitkText>{contentText}</UitkText>
      </UitkTableCell>
    </UitkTableRow>
  );
};

interface TripCostDialogProps {
  close: () => void;
  hotelCostList: TripCostEstimatorHotelCost[];
  isOpen: boolean;
  onClickList: (hotelCost?: TripCostEstimatorHotelCost) => void;
  title: string;
}

const TripCostDialogComponent = ({ close, hotelCostList, onClickList, title, isOpen }: TripCostDialogProps) => {
  const keyHelper = new ItemKeyHelper("trip-cost-dialog-list");
  const { formatText } = useLocalization();
  const triggerRef = React.createRef<HTMLElement>();
  const l10n = {
    month: () => formatText("tripCostEstimator.month"),
    price: () => formatText("tripCostEstimator.price"),
    footerText: () => formatText("tripCostEstimator.footerDialog", 2018, "Expedia.com"),
    allPrice: () => formatText("tripCostEstimator.allPrices", "USD"),
  };

  return (
    <UitkSheet triggerRef={triggerRef} isVisible={isOpen} type="centered" centeredSheetSize="small">
      <UitkToolbar
        toolbarTitle={title}
        navigationContent={{
          navIconLabel: "Close",
          onClick: close,
        }}
      />
      <UitkSheetContent>
        <UitkTable>
          <UitkTableHead>
            <UitkTableRow>
              <UitkTableCell>
                <UitkText size={400}>{l10n.month()}</UitkText>
              </UitkTableCell>
              <UitkTableCell>
                <UitkText size={400}>{l10n.price()}</UitkText>
              </UitkTableCell>
            </UitkTableRow>
          </UitkTableHead>
          <UitkTableBody>
            {hotelCostList.map((hotelCost: TripCostEstimatorHotelCost) => {
              const handleClick = () => onClickList(hotelCost);
              const { avgTwoStarsCost, avgFourStarsCost, month: label } = hotelCost;

              return (
                <ListItem
                  text={label}
                  prices={[avgTwoStarsCost, avgFourStarsCost]}
                  key={keyHelper.next()}
                  onClick={handleClick}
                />
              );
            })}
          </UitkTableBody>
        </UitkTable>
        <UitkText size={200}>{l10n.footerText()}</UitkText>
        <UitkText size={200}>{l10n.allPrice()}</UitkText>
      </UitkSheetContent>
    </UitkSheet>
  );
};

export const TripCostDialog = observer(TripCostDialogComponent);

export default TripCostDialog;
